import { uuidModule } from "@/modules/uuid";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { IUpdateSpaceAccountSettingsOperation } from "@/store/sync/operations/types";
import { OptimisticSyncUpdate, SyncModelData, SyncOperationKind } from "@/store/sync/types";
import {
  CalendarSettings,
  ChatSettings,
  MemAgentSettings,
  SpaceAccountSettingsModelData,
  SpaceAccountSettingsPayload,
  VoiceSettings,
} from "@/store/space-account-settings/types";

export class UpdateSpaceAccountSettingsOperation extends BaseSyncOperation<IUpdateSpaceAccountSettingsOperation> {
  get operationKind(): SyncOperationKind {
    return "UPDATE_SPACE_ACCOUNT_SETTINGS";
  }

  async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const optimisticUpdates: OptimisticSyncUpdate<SyncModelData>[] = [];
    const settingsUpdate = this.generateSpaceAccountSettingsUpdate();

    if (settingsUpdate) {
      optimisticUpdates.push(settingsUpdate);
    }

    return optimisticUpdates;
  }

  async triggerRecompute(): Promise<void> {
    await this.store.spaceAccountSettings.recompute(this.payload.space_account_id);
  }

  protected generateSpaceAccountSettingsUpdate(): OptimisticSyncUpdate<SpaceAccountSettingsModelData> | null {
    const existingVoiceSettings = this.store.spaceAccountSettings.voiceSettings;
    const existingCalendarSettings = this.store.spaceAccountSettings.calendarSettings;
    const existingChatSettings = this.store.spaceAccountSettings.chatSettings;
    const existingMemAgentSettings = this.store.spaceAccountSettings.memAgentSettings;

    // Build the merged settings payload by applying the patch on top of existing settings
    const mergedSettings: SpaceAccountSettingsPayload = {};

    // Merge voice settings if provided in patch or existing
    if (this.payload.settings.voice || existingVoiceSettings) {
      const mergedVoice: VoiceSettings = {
        custom_vocabulary:
          this.payload.settings.voice?.custom_vocabulary ??
          existingVoiceSettings?.custom_vocabulary ??
          [],
        processing_instructions:
          this.payload.settings.voice?.processing_instructions ??
          existingVoiceSettings?.processing_instructions ??
          "",
      };
      mergedSettings.voice = mergedVoice;
    }

    // Merge calendar settings if provided in patch or existing
    if (this.payload.settings.calendar || existingCalendarSettings) {
      const mergedCalendar: CalendarSettings = {
        hide_calendar_integration_shill:
          this.payload.settings.calendar?.hide_calendar_integration_shill ??
          existingCalendarSettings?.hide_calendar_integration_shill ??
          false,
        enable_calendar_event_notifications:
          this.payload.settings.calendar?.enable_calendar_event_notifications ??
          existingCalendarSettings?.enable_calendar_event_notifications ??
          false,
        enable_unscheduled_meeting_notifications:
          this.payload.settings.calendar?.enable_unscheduled_meeting_notifications ??
          existingCalendarSettings?.enable_unscheduled_meeting_notifications ??
          false,
        show_calendar_events_with_no_attendees:
          this.payload.settings.calendar?.show_calendar_events_with_no_attendees ??
          existingCalendarSettings?.show_calendar_events_with_no_attendees ??
          true,
      };
      mergedSettings.calendar = mergedCalendar;
    }

    // Merge chat settings if provided in patch or existing
    if (this.payload.settings.chat || existingChatSettings) {
      const mergedChat: ChatSettings = {
        instructions:
          this.payload.settings.chat?.instructions ?? existingChatSettings?.instructions ?? "",
      };
      mergedSettings.chat = mergedChat;
    }

    // Merge Mem Agent settings if provided in patch or existing
    const memAgentSettingsPatch = this.payload.settings.mem_agent;
    const hasMemAgentSettingsPatch = memAgentSettingsPatch != null;
    const hasMemAgentDismissedAtPatch =
      hasMemAgentSettingsPatch &&
      Object.prototype.hasOwnProperty.call(memAgentSettingsPatch, "reminders_callout_dismissed_at");
    const hasMemAgentIsDismissiblePatch =
      hasMemAgentSettingsPatch &&
      Object.prototype.hasOwnProperty.call(
        memAgentSettingsPatch,
        "reminders_callout_is_dismissible"
      );
    const hasMemAgentProactiveTrialBannerDismissedPatch =
      hasMemAgentSettingsPatch &&
      Object.prototype.hasOwnProperty.call(
        memAgentSettingsPatch,
        "proactive_trial_banner_is_dismissed"
      );
    const hasMemAgentHoldToTalkKeyPatch =
      hasMemAgentSettingsPatch &&
      Object.prototype.hasOwnProperty.call(memAgentSettingsPatch, "hold_to_talk_key");
    const hasMemAgentHoldToTalkIsEnabledPatch =
      hasMemAgentSettingsPatch &&
      Object.prototype.hasOwnProperty.call(memAgentSettingsPatch, "hold_to_talk_is_enabled");

    if (hasMemAgentSettingsPatch || existingMemAgentSettings) {
      const mergedMemAgent: MemAgentSettings = {
        reminders_callout_dismissed_at: hasMemAgentDismissedAtPatch
          ? (memAgentSettingsPatch?.reminders_callout_dismissed_at ?? null)
          : (existingMemAgentSettings?.reminders_callout_dismissed_at ?? null),
        reminders_callout_is_dismissible: hasMemAgentIsDismissiblePatch
          ? (memAgentSettingsPatch?.reminders_callout_is_dismissible ?? false)
          : (existingMemAgentSettings?.reminders_callout_is_dismissible ?? false),
        proactive_trial_banner_is_dismissed: hasMemAgentProactiveTrialBannerDismissedPatch
          ? (memAgentSettingsPatch?.proactive_trial_banner_is_dismissed ?? false)
          : (existingMemAgentSettings?.proactive_trial_banner_is_dismissed ?? false),
        hold_to_talk_key: hasMemAgentHoldToTalkKeyPatch
          ? memAgentSettingsPatch?.hold_to_talk_key
          : existingMemAgentSettings?.hold_to_talk_key,
        hold_to_talk_is_enabled: hasMemAgentHoldToTalkIsEnabledPatch
          ? (memAgentSettingsPatch?.hold_to_talk_is_enabled ?? true)
          : (existingMemAgentSettings?.hold_to_talk_is_enabled ?? true),
      };
      mergedSettings.mem_agent = mergedMemAgent;
    }

    const syncUpdate: OptimisticSyncUpdate<SpaceAccountSettingsModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: {
        model_id: this.payload.space_account_id,
        model_kind: "SPACE_ACCOUNT_SETTINGS",
        model_version: 0,
        model_data: {
          owned_by_space_account_id: this.payload.space_account_id,
          settings: mergedSettings,
        },
        model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
      },
    };

    return syncUpdate;
  }
}
